// views/Geradoras/Analise/CurvaGeracao.tsx
//
// O gráfico que o chamado pede: "medindo com gráficos a potência e a geração de
// hora em hora". Barras = energia gerada no balde (kWh); linha = potência média
// no balde (kW), em eixo próprio à direita — as duas grandezas têm ordens de
// grandeza diferentes e dividir o eixo achataria a curva de potência.
import React from 'react';
import {
    ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
    CartesianGrid, Tooltip, Legend, ReferenceLine, Cell,
} from 'recharts';
import { CloudOff } from 'lucide-react';
import { rotuloBucket, type Granularidade, type PontoSerie } from '../geradorasSeries';

// Amarelo da marca para a energia (é o produto), azul para potência — o mesmo
// azul que o Painel já usa em "Potência Instantânea".
const COR_GERACAO = '#F6A937';
const COR_GERACAO_FRACA = '#fcd9a0';
const COR_POTENCIA = '#2563eb';
const COR_CONTRASTE = '#94a3b8';

interface LinhaGrafico {
    chave: string;
    rotulo: string;
    geracao: number | null;
    potenciaKw: number | null;
    contraste: number | null;
    /** Balde com menos da metade das amostras usuais: pinta claro e avisa. */
    fraco: boolean;
    amostras: number;
}

interface Props {
    pontos: PontoSerie[];
    /** Série do período de contraste, já alinhada por posição. */
    contraste?: (number | null)[];
    rotuloContraste?: string;
    granularidade: Granularidade;
    /** Média do período, desenhada como linha tracejada de referência. */
    mediaKwh?: number | null;
    /** Mediana de amostras por balde — base do aviso de amostragem baixa. */
    medianaAmostras?: number | null;
    altura?: number;
}

const fmtKwh = (v: number) => `${v.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} kWh`;
const fmtKw = (v: number) => `${v.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} kW`;

const TooltipCustom: React.FC<any> = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const linha: LinhaGrafico | undefined = payload[0]?.payload;
    return (
        <div className="rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-xl px-3 py-2 text-xs">
            <div className="font-bold text-gray-900 dark:text-white mb-1.5">{label}</div>
            {payload.map((p: any) => (
                <div key={p.dataKey} className="flex items-center gap-2 tabular-nums">
                    <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
                    <span className="text-gray-500 dark:text-gray-400">{p.name}:</span>
                    <span className="font-semibold text-gray-900 dark:text-white">
                        {p.value == null ? 'sem dado' : p.dataKey === 'potenciaKw' ? fmtKw(p.value) : fmtKwh(p.value)}
                    </span>
                </div>
            ))}
            {linha?.fraco && (
                <div className="mt-1.5 pt-1.5 border-t border-gray-100 dark:border-gray-700 text-[11px] text-amber-600 dark:text-amber-400 font-semibold">
                    Só {linha.amostras} amostra(s) nesta hora — valor pouco confiável
                </div>
            )}
        </div>
    );
};

const CurvaGeracao: React.FC<Props> = ({
    pontos, contraste, rotuloContraste, granularidade, mediaKwh, medianaAmostras, altura = 320,
}) => {
    const corte = medianaAmostras && medianaAmostras > 1 ? medianaAmostras / 2 : 0;

    const dados: LinhaGrafico[] = pontos.map((p, i) => ({
        chave: p.bucket,
        rotulo: rotuloBucket(p.bucket, granularidade),
        geracao: p.geracao_kwh,
        potenciaKw: p.potencia_media_w == null ? null : p.potencia_media_w / 1000,
        contraste: contraste?.[i] ?? null,
        fraco: granularidade === 'hora' && corte > 0 && p.amostras < corte,
        amostras: p.amostras,
    }));

    if (dados.length === 0) {
        return (
            <div
                className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-600 flex flex-col items-center justify-center gap-2 text-gray-500 dark:text-gray-400"
                style={{ height: altura }}
            >
                <CloudOff size={26} className="opacity-50" />
                <p className="text-sm font-medium">Sem telemetria no período selecionado</p>
            </div>
        );
    }

    return (
        <div style={{ height: altura }}>
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={dados} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#37415122" vertical={false} />
                    <XAxis
                        dataKey="rotulo"
                        tick={{ fontSize: 11 }}
                        interval="preserveStartEnd"
                        minTickGap={16}
                    />
                    <YAxis
                        yAxisId="kwh"
                        tick={{ fontSize: 11 }}
                        width={54}
                        tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)}
                    />
                    <YAxis
                        yAxisId="kw"
                        orientation="right"
                        tick={{ fontSize: 11, fill: COR_POTENCIA }}
                        width={52}
                        tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(1)} MW` : `${Math.round(v)} kW`}
                    />
                    <Tooltip content={<TooltipCustom />} cursor={{ fill: '#94a3b81a' }} />
                    <Legend
                        wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
                        iconType="circle"
                        iconSize={8}
                    />

                    {contraste && (
                        <Bar
                            yAxisId="kwh"
                            dataKey="contraste"
                            name={rotuloContraste || 'Período anterior'}
                            fill={COR_CONTRASTE}
                            fillOpacity={0.45}
                            radius={[4, 4, 0, 0]}
                            maxBarSize={38}
                        />
                    )}

                    <Bar
                        yAxisId="kwh"
                        dataKey="geracao"
                        name="Geração"
                        // fill aqui é só para a LEGENDA: a cor de cada barra vem
                        // das <Cell> abaixo (barra clara = hora mal amostrada).
                        fill={COR_GERACAO}
                        radius={[4, 4, 0, 0]}
                        maxBarSize={38}
                    >
                        {/* Balde mal amostrado sai em tom claro: o número existe,
                            mas o operador precisa saber que ele é frouxo. */}
                        {dados.map((d, i) => (
                            <Cell key={i} fill={d.fraco ? COR_GERACAO_FRACA : COR_GERACAO} />
                        ))}
                    </Bar>

                    <Line
                        yAxisId="kw"
                        type="monotone"
                        dataKey="potenciaKw"
                        name="Potência média"
                        stroke={COR_POTENCIA}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 4 }}
                        connectNulls={false}
                    />

                    {mediaKwh != null && mediaKwh > 0 && (
                        <ReferenceLine
                            yAxisId="kwh"
                            y={mediaKwh}
                            stroke="#64748b"
                            strokeDasharray="5 4"
                            strokeWidth={1.5}
                            label={{
                                value: `média ${fmtKwh(mediaKwh)}`,
                                position: 'insideTopRight',
                                fontSize: 10,
                                fill: '#64748b',
                            }}
                        />
                    )}
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    );
};

export default CurvaGeracao;
